import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Bell,
  CheckCircle,
  AlertCircle,
  Star,
  Phone,
  MessageSquare,
  Clock,
  ArrowRightLeft,
  AlertTriangle,
  BarChart3,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { getNotifications } from "../../api/notifications";
import { hasAdminAccess } from "../../utils/roleAccess";

const Notifications = () => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
  };

  // Map API categories to notification types
  const notificationTypes = {
    new_lead_assigned: {
      label: "Hot/Warm Lead Assigned",
      icon: Star,
      color: "bg-red-100 text-red-700",
      priority: "high",
    },
    callback_reminder: {
      label: "Callback/Follow-Up Reminder",
      icon: Clock,
      color: "bg-blue-100 text-blue-700",
      priority: "medium",
    },
    ai_handoff: {
      label: "Live Chat or AI Handoff",
      icon: ArrowRightLeft,
      color: "bg-purple-100 text-purple-700",
      priority: "high",
    },
    hot_lead_escalation: {
      label: "Unattended Hot Lead Escalation",
      icon: AlertTriangle,
      color: "bg-yellow-100 text-yellow-700",
      priority: "high",
    },
    daily_summary: {
      label: "Daily Agent Summary",
      icon: BarChart3,
      color: "bg-green-100 text-green-700",
      priority: "medium",
    },
    system_alert: {
      label: "System Alert",
      icon: AlertCircle,
      color: "bg-gray-100 text-gray-700",
      priority: "high",
    },
  };

  const getNotification = async () => {
    try {
      setLoading(true);
      const response = await getNotifications();
      console.log("API Response:", response);

      // Transform API data to match component format
      const transformedNotifications = response?.map((notification, index) => ({
        id: notification.id,
        type: notification.category || "system_alert",
        title: notification.title || "Notification",
        message: notification.description || "No description available",
        timestamp: notification.created_at,
        read: !notification.is_visible, // Assuming is_visible false means read
        priority:
          notificationTypes[notification.category]?.priority || "medium",
        data: notification, // Keep original data for reference
      }));

      setNotifications(transformedNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNotification();
  }, []);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now - date) / (1000 * 60);

    if (diffInMinutes < 1) {
      return "Just now";
    } else if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)}m ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const markAsRead = (id) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
    toast.success("Notification marked as read");
  };

  const markAllAsRead = () => {
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, read: true }))
    );
    toast.success("All notifications marked as read");
  };

  const deleteNotification = (id) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
    toast.success("Notification deleted");
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-500";
      case "medium":
        return "bg-yellow-500";
      case "low":
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#55C5D0] leading-snug">
            Notifications
          </h2>
          <p className="text-gray-600 font-normal leading-relaxed">
            {isAdmin
              ? "Monitor system alerts and team notifications"
              : "Stay updated with your leads and alerts"}
          </p>
        </div>
        <button
          onClick={markAllAsRead}
          className="px-4 py-2 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] transition-colors flex items-center space-x-2"
        >
          <CheckCircle className="w-4 h-4" />
          <span>Mark All Read</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {notifications.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Bell className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Unread</p>
              <p className="text-2xl font-bold text-gray-900">
                {notifications.filter((n) => !n.read).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">High Priority</p>
              <p className="text-2xl font-bold text-gray-900">
                {notifications.filter((n) => n.priority === "high").length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#55C5D0] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No notifications
            </h3>
            <p className="text-gray-600">You're all caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {notifications.map((notification) => {
              const Icon = notificationTypes[notification.type]?.icon || Bell;
              const typeConfig =
                notificationTypes[notification.type] ||
                notificationTypes.system_alert;

              return (
                <div
                  key={notification.id}
                  className={`p-6 hover:bg-gray-50 transition-colors ${
                    !notification.read ? "bg-blue-50/50" : ""
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    {/* Icon */}
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeConfig.color}`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3
                              className={`text-sm font-medium ${
                                !notification.read
                                  ? "text-gray-900"
                                  : "text-gray-700"
                              }`}
                            >
                              {notification.title}
                            </h3>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-[#55C5D0] rounded-full"></div>
                            )}
                            <div
                              className={`w-2 h-2 rounded-full ${getPriorityColor(
                                notification.priority
                              )}`}
                            ></div>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          <div className="flex items-center space-x-4 mt-2">
                            <span className="text-xs text-gray-500 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatTimestamp(notification.timestamp)}
                            </span>
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${typeConfig.color}`}
                            >
                              {typeConfig.label.toUpperCase()}
                            </span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-2">
                          {!notification.read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="text-xs text-[#55C5D0] hover:underline"
                            >
                              Mark as read
                            </button>
                          )}
                          {/* <button
                            onClick={() => deleteNotification(notification.id)}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Delete
                          </button> */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
